import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { CalendarCheck, ArrowRight, Sparkles } from "lucide-react";
import { useDemo } from "@/components/landing/DemoContext";

export const PageHero = ({ overline, title, highlight, subtitle, hideCtas = false }) => {
  const openDemo = useDemo();
  return (
    <section data-testid="page-hero" className="relative overflow-hidden pt-20 pb-10 sm:pt-44 sm:pb-20">
      <div className="absolute inset-0 -z-10 bg-grid" />
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-transparent to-background" />
      <div className="pointer-events-none absolute left-1/2 top-0 -z-10 h-[360px] w-[680px] -translate-x-1/2 rounded-full bg-violet-600/15 blur-[130px]" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl"
        >
          {overline && (
            <p className="hidden sm:block text-xs font-semibold uppercase tracking-[0.2em] text-violet-500">{overline}</p>
          )}
          <h1 className="mt-0 sm:mt-4 font-display text-[2.05rem] font-black leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
            {title} {highlight && <span className="text-gradient">{highlight}</span>}
          </h1>
          {subtitle && (
            <p className="mt-3 sm:mt-6 max-w-2xl text-[14px] leading-relaxed text-muted-foreground sm:text-lg">{subtitle}</p>
          )}

          {/* CTA pair — primary always, secondary desktop-only */}
          {!hideCtas && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.18 }}
              className="mt-5 sm:mt-8 flex flex-wrap items-center gap-2 sm:gap-3"
            >
              <button
                type="button"
                onClick={openDemo}
                data-testid="pagehero-book-demo"
                className="group inline-flex h-12 w-full sm:w-auto items-center justify-center gap-2 rounded-xl bg-foreground px-7 text-sm font-semibold text-background shadow-lg shadow-foreground/15 transition-all hover:-translate-y-0.5 hover:shadow-xl sm:text-[15px]"
              >
                <CalendarCheck className="h-4 w-4" />
                Book a Demo
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </button>
              <Link
                to="/#meet-clinic-os"
                data-testid="pagehero-try-clinic-os"
                onClick={(e) => {
                  if (window.location.pathname === "/") {
                    e.preventDefault();
                    document
                      .getElementById("meet-clinic-os")
                      ?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }
                }}
                className="hidden sm:inline-flex h-12 items-center justify-center gap-2 rounded-xl border border-violet-500/40 bg-gradient-to-r from-violet-500/12 via-blue-500/10 to-emerald-500/10 px-5 text-sm font-bold text-foreground/90 backdrop-blur transition-all hover:-translate-y-0.5 hover:border-violet-500/60 hover:bg-violet-500/15 sm:text-[15px]"
              >
                <Sparkles className="h-4 w-4 text-violet-500" />
                Try Clinic OS
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>
  );
};
